import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {Box, Button, Link} from '@mui/material';
import {MRT_ColumnDef} from 'material-react-table';

import {
  formatMarketplaceItemType,
  MARKETPLACE_ITEM_TYPES,
  MarketplaceItem,
  MarketplaceItemType,
  useGetMarketplaceItems,
} from '@entities/marketplace';
import {MrTable, rightAlignmentColumnProps} from '@shared/components';
import {FilterRow, SelectFilter} from '@shared/components/filters';
import {
  entityTableCommonProps,
  ROUTE__MARKETPLACE_ITEM_CREATE,
  ROUTE__MARKETPLACE_ITEM_EDIT,
} from '@shared/constants';
import {useUrlState} from '@shared/hooks';

import {PaginationState} from '@/shared/types';

type UrlState = PaginationState & {
  type: MarketplaceItemType | null;
};

const marketplaceTypeFilterOptions = [
  {value: null, label: 'Type'},
  ...MARKETPLACE_ITEM_TYPES.map(type => ({
    value: type,
    label: formatMarketplaceItemType(type),
  })),
];

const columns: MRT_ColumnDef<MarketplaceItem>[] = [
  {
    accessorKey: 'title',
    header: 'Title',
    size: 280,
    Cell: ({row}) => {
      const {id, title, imageUrl} = row.original;
      return (
        <Link
          component={RouterLink}
          to={ROUTE__MARKETPLACE_ITEM_EDIT.replace(':id', id)}
          display="flex"
          alignItems="center"
          gap={1}
          underline="hover"
        >
          {imageUrl && (
            <img
              src={imageUrl}
              width={32}
              height={32}
              alt={title}
              style={{borderRadius: 6, objectFit: 'cover', flexShrink: 0}}
            />
          )}
          {title}
        </Link>
      );
    },
  },
  {
    accessorKey: 'type',
    header: 'Type',
    size: 120,
    Cell: ({row}) => formatMarketplaceItemType(row.original.type),
  },
  {
    accessorKey: 'price',
    header: 'Price',
    size: 140,
    Cell: ({row}) => {
      const {price} = row.original;
      return price == null ? 'Free' : price.toLocaleString();
    },
    ...rightAlignmentColumnProps,
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 360,
  },
];

export const MarketplacePage = () => {
  const navigate = useNavigate();
  const {urlState, setUrl} = useUrlState<UrlState>({
    pageIndex: 0,
    pageSize: 10,
    type: null,
  });

  const {data, isPending, error} = useGetMarketplaceItems({
    page: urlState.pageIndex + 1,
    pageSize: urlState.pageSize,
    type: urlState.type ?? undefined,
  });

  return (
    <MrTable
      error={error}
      columns={columns}
      data={data?.items ?? []}
      renderTopToolbarCustomActions={() => {
        return (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            gap={2}
          >
            <Box display="flex" alignItems="center" sx={{flexShrink: 0}}>
              <Button
                onClick={() => {
                  navigate(ROUTE__MARKETPLACE_ITEM_CREATE);
                }}
              >
                Create item
              </Button>
            </Box>
            <FilterRow>
              <SelectFilter
                value={urlState.type}
                onChange={type =>
                  setUrl(prev => ({
                    ...prev,
                    pageIndex: 0,
                    type,
                  }))
                }
                options={marketplaceTypeFilterOptions}
              />
            </FilterRow>
          </Box>
        );
      }}
      enableColumnOrdering
      enableColumnDragging
      enableHiding
      enablePagination
      manualPagination
      enableColumnPinning
      onPaginationChange={updater =>
        setUrl(prev => {
          const nextPagination =
            typeof updater === 'function' ? updater(prev) : updater;

          return {
            ...prev,
            ...nextPagination,
          };
        })
      }
      rowCount={data?.total ?? 0}
      state={{
        isLoading: isPending,
        pagination: {
          pageIndex: urlState.pageIndex,
          pageSize: urlState.pageSize,
        },
      }}
      {...entityTableCommonProps}
      enableRowActions={false}
    />
  );
};
